import { StripeError } from '@stripe/stripe-js';
import {
  BILLING_UNKNOWN_ERROR,
  BillingErrorCode,
  BillingErrorPayload,
  getBillingCodeForStripeCode,
  getCategoryForCode,
  normalizeBillingError,
} from '@utils/billingErrors';

export const ADD_PAYMENT_METHOD_FALLBACK_ERROR =
  'We could not add your payment method. Please try a different card or contact support@theinformation.com.';

export const BILLING_ADDRESS_FALLBACK_ERROR =
  'We could not save your billing address. Please check the address and try again.';

export const SUBSCRIPTION_PAYMENT_FALLBACK_ERROR =
  'We could not process your payment. Please try a different card or contact support@theinformation.com.';

export const CLIENT_BILLING_FALLBACK_ERROR = 'An unexpected error occurred. Please try again.';

type IntentErrorLike = {
  message?: string | null;
} | null;

// A SetupIntent, or the past-due invoice's PaymentIntent confirmed from the update-payment page.
type IntentLike = {
  last_setup_error?: IntentErrorLike;
  last_payment_error?: IntentErrorLike;
} | null;

type StripeErrorLike = {
  code?: string;
  decline_code?: string;
  message?: string;
  type?: string;
} | null;

export function clientBillingError(
  message?: string,
  fallback: string = CLIENT_BILLING_FALLBACK_ERROR,
  flow?: string
): BillingErrorPayload {
  return {
    ...BILLING_UNKNOWN_ERROR,
    message: message || fallback,
    cta_type: 'try_again',
    cta_link: null,
    ...(flow ? { flow } : {}),
  };
}

// Stripe.js errors never reach the classifier in app/services/billing_error.rb, so
// they are classified here against the same Stripe code map.
//
// cta_type stays pinned to try_again: letting error_code drive the CTA would change
// the button users see, which is a separate ticket.
function classifiedStripeError(
  stripeError: StripeErrorLike,
  fallbackCode: BillingErrorCode,
  message: string,
  flow?: string
): BillingErrorPayload {
  const code = getBillingCodeForStripeCode(stripeError?.code) ?? fallbackCode;

  return {
    ...BILLING_UNKNOWN_ERROR,
    error_code: code,
    error_category: getCategoryForCode(code),
    message,
    cta_type: 'try_again',
    cta_link: null,
    // Every code in STRIPE_CODE_MAP is recoverable in the registry; the unknown
    // fallback is not, and must not contradict the registry on the same code.
    recoverable: code !== 'billing_unknown_error',
    stripe_code: stripeError?.code ?? null,
    stripe_decline_code: stripeError?.decline_code ?? null,
    stripe_message: stripeError?.message ?? null,
    ...(flow ? { flow } : {}),
  };
}

export function stripeSetupErrorPayload(
  error?: StripeError | null,
  intent?: IntentLike,
  flow?: string
): BillingErrorPayload {
  const message =
    error?.message ||
    intent?.last_setup_error?.message ||
    intent?.last_payment_error?.message ||
    ADD_PAYMENT_METHOD_FALLBACK_ERROR;

  return classifiedStripeError(error ?? null, 'billing_unknown_error', message, flow);
}

export function addPaymentMethodErrorPayload(
  error?: StripeErrorLike,
  flow?: string
): BillingErrorPayload {
  return classifiedStripeError(
    error ?? null,
    'billing_unknown_error',
    error?.message || ADD_PAYMENT_METHOD_FALLBACK_ERROR,
    flow
  );
}

export function billingAddressErrorPayload(): BillingErrorPayload {
  return clientBillingError(BILLING_ADDRESS_FALLBACK_ERROR);
}

export function stripeErrorPayload(
  stripeError?: StripeErrorLike,
  flow = 'subscribe'
): BillingErrorPayload {
  const isShowable = stripeError?.type === 'card_error' && Boolean(stripeError.message);
  const message = isShowable ? stripeError.message : SUBSCRIPTION_PAYMENT_FALLBACK_ERROR;

  return classifiedStripeError(stripeError ?? null, 'billing_payment_failed', message, flow);
}

export { normalizeBillingError as apiErrorPayload };
